// Edge provenance model (ARCHITECTURE.md §4.2).
//
// Every GraphEdge carries an array of Evidence describing *why* the edge
// exists. Evidence is the basis for the deterministic-vs-candidate
// classification: high-confidence static analysis (e.g. static_import) versus
// lower-confidence heuristics or LLM inference (e.g. llm_semantic_link).
//
// The Zod schema validates at trust boundaries (cache deserialization, plugin
// input); the inferred TypeScript types are the compile-time source of truth.

import { z } from "zod";

// ---------------------------------------------------------------------------
// Evidence type
// ---------------------------------------------------------------------------

export const EVIDENCE_TYPES = [
  "static_import",
  "dynamic_import",
  "manifest_dependency",
  "path_pattern_match",
  "naming_convention",
  "matched_schema_name",
  "nearby_import",
  "tool_output_field_similarity",
  "llm_semantic_link",
] as const;

export const EvidenceTypeSchema = z.enum(EVIDENCE_TYPES);

export type EvidenceType = z.infer<typeof EvidenceTypeSchema>;

// ---------------------------------------------------------------------------
// Evidence
// ---------------------------------------------------------------------------

export const EvidenceSchema = z.object({
  type: EvidenceTypeSchema,
  detail: z.string(),
  /** 1-based source line the evidence was observed on, when applicable. */
  line: z.number().int().positive().optional(),
  /** Confidence in the range 0.0 – 1.0 inclusive. */
  confidence: z.number().min(0).max(1),
});

export type Evidence = z.infer<typeof EvidenceSchema>;
